import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { AlertTriangle } from 'lucide-react';
import { db } from '@/db';
import { ErrorBoundary } from '@/components/feedback/error-boundary';
import { LoadingScreen } from '@/components/feedback/loading-screen';
import { ToastViewport } from '@/components/feedback/toast';
import { Button } from '@/components/ui/button';
import { Onboarding } from '@/features/onboarding';
import { useThemeEffect } from '@/hooks/use-theme';
import { completeOnboarding, initializeApp } from './bootstrap';
import { PwaUpdatePrompt } from './pwa-update-prompt';
import { router } from './router';

export function App() {
  useThemeEffect();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const settings = useLiveQuery(
    async () => (ready ? ((await db.userSettings.toCollection().first()) ?? null) : null),
    [ready],
    null
  );

  useEffect(() => {
    void initializeApp()
      .then(() => setReady(true))
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason : new Error('Initialisierung fehlgeschlagen.'))
      );
  }, []);

  if (error) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-6">
        <section className="max-w-md rounded-2xl border border-danger/25 bg-surface p-6">
          <AlertTriangle aria-hidden="true" className="size-6 text-danger" />
          <h1 className="mt-4 font-display text-2xl font-bold">
            Lokale Datenbank konnte nicht geöffnet werden
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted">{error.message}</p>
          <Button className="mt-5" onClick={() => window.location.reload()}>
            Erneut versuchen
          </Button>
        </section>
      </main>
    );
  }
  if (!ready || !settings) return <LoadingScreen />;

  return (
    <ErrorBoundary>
      {settings.onboardingCompleted ? (
        <RouterProvider router={router} />
      ) : (
        <Onboarding
          initialData={{
            name: settings.name,
            wakeTime: settings.wakeTime,
            sleepTime: settings.bedTime,
            areas: settings.activeAreas,
            dailyGoals: {
              schoolMinutes: settings.dailyGoals.schoolMinutes,
              frenchMinutes: settings.dailyGoals.frenchMinutes,
              youtubeMinutes: settings.dailyGoals.youtubeMinutes,
              novelChapters: settings.dailyGoals.novelChapters,
              pushUpCurrentMax: settings.currentPushUpMaximum,
              pushUpLongTermGoal: settings.longTermPushUpGoal,
              squatsDaily: settings.dailyGoals.squats
            }
          }}
          onComplete={completeOnboarding}
        />
      )}
      <PwaUpdatePrompt />
      <ToastViewport />
    </ErrorBoundary>
  );
}
